import { z } from 'zod';

import type { ZodUseMutation, ZodUseQuery } from '@/utils/zodReactQuery';

/**
 * The wire contract with the backend uses `snake_case` keys, which MSW
 * reproduces byte for byte (see `mocks/handlers/admin/playground/`). These
 * types describe the same payload **after** the shared `AGENDA_PRO_API`
 * client's `useCamelCase: true` transform, which every other service in this
 * repo already relies on — so the keys here are `camelCase`, matching
 * `Conversations.schema.ts` / `Evaluations.schema.ts`, not the wire
 * contract's literal field names. Verified empirically: `API.post` sends a
 * `camelCase` body as `snake_case` over the wire and parses a `snake_case`
 * response back into `camelCase` — the two are not implicitly equal.
 */
export const graphVersionSchema = z.enum(['v1', 'v2']);
export type GraphVersion = z.infer<typeof graphVersionSchema>;

export const playgroundChannelSchema = z.enum(['conversation', 'whatsapp', 'email']);
export type PlaygroundChannel = z.infer<typeof playgroundChannelSchema>;

export type PlaygroundAskRequest = {
	question: string;
	threadId?: string;
	graphVersion?: GraphVersion | null;
	country?: string;
	companyId?: string;
	channel?: PlaygroundChannel;
	/** Only meaningful when `graphVersion` is `'v2'` — omitted otherwise (`front#SPEC-007`). */
	area?: string | null;
	/** Business-named account fields the backend applies on top of the real
	 * (with `companyId`) or empty (without it) snapshot. */
	accountOverrides?: AccountOverrides;
};

export type AccountOverrides = {
	vertical?: string;
	saasPlan?: string;
	monthlyGmvUsd?: number;
	locationsCount?: number;
};

export const graphVersionSourceSchema = z.enum(['explicit', 'rule', 'ramp', 'default', 'sticky']);
export type GraphVersionSource = z.infer<typeof graphVersionSourceSchema>;

export const playgroundAskResponseSchema = z.object({
	reply: z.string(),
	threadId: z.string(),
	graphVersion: graphVersionSchema,
	graphVersionSource: graphVersionSourceSchema,
	matchedRule: z.string().nullable(),
	shortcircuit: z.string().nullable(),
	wouldHandoffTo: z.string().nullable(),
	traceId: z.string(),
	/** Which area answered the turn — `null` on `v1` (`front#SPEC-007`). */
	area: z.string().nullable(),
	/** Tools the model called during the turn, without results.
	 * `args` keys arrive camelCased by the shared HTTP client. Defaulted so a
	 * backend that does not send them yet still parses instead of breaking
	 * the chat. */
	toolCalls: z.array(z.object({ name: z.string(), args: z.record(z.unknown()) })).default([]),
	latencyMs: z.number().nullable().default(null),
});
export type PlaygroundAskResponse = z.infer<typeof playgroundAskResponseSchema>;

export const graphRoutingRuleSchema = z.object({
	id: z.string(),
	tier: z.array(z.number().int()).optional(),
	companyIds: z.array(z.string()).optional(),
	version: graphVersionSchema,
});
export type GraphRoutingRule = z.infer<typeof graphRoutingRuleSchema>;

/** An area the `v2` router can dispatch a turn to (`front#SPEC-007`). */
export const graphRoutingAreaSchema = z.object({
	name: z.string(),
	description: z.string(),
	capabilities: z.array(z.string()),
});
export type GraphRoutingArea = z.infer<typeof graphRoutingAreaSchema>;

export const graphRoutingResponseSchema = z.object({
	default: graphVersionSchema,
	versions: z.array(graphVersionSchema),
	rules: z.array(graphRoutingRuleSchema),
	ramp: z.record(graphVersionSchema, z.number()),
	areas: z.array(graphRoutingAreaSchema),
	/** Companies offered in the subject selector; may be empty.
	 * Defaulted because the dashboard and the routing page parse this too. */
	testCompanies: z.array(z.object({ companyId: z.string(), label: z.string() })).default([]),
});
export type GraphRoutingResponse = z.infer<typeof graphRoutingResponseSchema>;

export const graphRoutingResolveResponseSchema = z.object({
	companyId: z.string(),
	tier: z.number().int().nullable(),
	graphVersion: graphVersionSchema,
	matchedRule: z.string().nullable(),
});
export type GraphRoutingResolveResponse = z.infer<typeof graphRoutingResolveResponseSchema>;

/** The real account profile a playground turn starts from. */
export const playgroundAccountSchema = z.object({
	companyId: z.string(),
	name: z.string().nullable(),
	vertical: z.string().nullable(),
	saasPlan: z.string().nullable(),
	monthlyGmvUsd: z.number().nullable(),
	locationsCount: z.number().nullable(),
});
export type PlaygroundAccount = z.infer<typeof playgroundAccountSchema>;

export const feedbackRatingSchema = z.enum(['good', 'bad']);
export type FeedbackRating = z.infer<typeof feedbackRatingSchema>;

export type PlaygroundFeedbackRequest = {
	threadId: string;
	traceId: string;
	rating: FeedbackRating;
	comment?: string;
};

export const playgroundFeedbackResponseSchema = z.object({ status: z.literal('saved') });
export type PlaygroundFeedbackResponse = z.infer<typeof playgroundFeedbackResponseSchema>;

export type UseAskPlayground = ZodUseMutation<PlaygroundAskResponse, PlaygroundAskRequest>;
export type UseGraphRouting = ZodUseQuery<GraphRoutingResponse, never>;
export type UseResolveGraphVersion = ZodUseQuery<GraphRoutingResolveResponse, { companyId: string }>;
export type UsePlaygroundAccount = ZodUseQuery<PlaygroundAccount, { companyId: string }>;
export type UseSavePlaygroundFeedback = ZodUseMutation<PlaygroundFeedbackResponse, PlaygroundFeedbackRequest>;
